import React, { KeyboardEvent, useEffect, useRef, useState } from 'react';
import {
    ConsiderationField,
    ConsiderationFieldChange,
    ConsiderationSection,
} from '../../../models/FlowOfFunds/flow-of-funds.model';
import { EM_DASH } from '../../../utils/flowOfFundsFormat';
import {
    getFieldKind,
    toDisplayValue,
    toWireValue,
} from '../../../utils/flow-of-funds/consideration-field-format';
import { useConsiderationAutosaveQueue } from './ConsiderationAutosaveQueueContext';
import { EditableConsiderationCellHistory } from './EditableConsiderationCellHistory';
import { EditableConsiderationCellInput } from './EditableConsiderationCellInput';
import './EditableConsiderationCell.scss';

const buildCellClassName = (
    variant: 'plain' | 'chip',
    isChanged: boolean,
    extra?: string
): string =>
    [
        'ff-editable-cell',
        variant === 'chip' ? 'ff-editable-cell--chip' : 'ff-editable-cell--plain',
        isChanged ? 'ff-editable-cell--changed' : '',
        extra ?? '',
    ]
        .filter(Boolean)
        .join(' ');

interface ReadOnlyConsiderationCellProps {
    field: ConsiderationField;
    wireValue: string | null;
    className: string;
    fieldChange?: ConsiderationFieldChange;
    engagementId: number;
}

const ReadOnlyConsiderationCell = ({
    field,
    wireValue,
    className,
    fieldChange,
    engagementId,
}: ReadOnlyConsiderationCellProps) => {
    const displayText = toDisplayValue(field, wireValue) || EM_DASH;
    return (
        <td className={className}>
            <span className="ff-editable-cell__value">
                <span
                    className={`ff-editable-cell__text${
                        displayText === EM_DASH ? ' ff-editable-cell__text--muted' : ''
                    }`}>
                    {displayText}
                </span>
                <EditableConsiderationCellHistory
                    field={field}
                    fieldChange={fieldChange}
                    engagementId={engagementId}
                />
            </span>
        </td>
    );
};

export interface EditableConsiderationCellProps {
    field: ConsiderationField;
    wireValue: string | null;
    section: ConsiderationSection;
    rowKey: number;
    editableFields: ConsiderationField[];
    fieldChanges: ConsiderationFieldChange[];
    engagementId: number;
    variant?: 'plain' | 'chip';
    className?: string;
}

export const EditableConsiderationCell = ({
    field,
    wireValue,
    section,
    rowKey,
    editableFields,
    fieldChanges,
    engagementId,
    variant = 'chip',
    className,
}: EditableConsiderationCellProps) => {
    const queue = useConsiderationAutosaveQueue();
    const isEditable = editableFields.includes(field);
    const fieldChange = fieldChanges.find((change) => change.field === field);
    const kind = getFieldKind(field);
    const isChanged = fieldChange?.isChanged ?? false;
    const cellClassName = buildCellClassName(variant, isChanged, className);

    const pendingValue = queue.pendingFieldValue(section, rowKey, field);
    const effectiveWireValue = pendingValue !== undefined ? pendingValue : wireValue;

    const [draft, setDraft] = useState(effectiveWireValue ?? '');
    const isEditingRef = useRef(false);
    const suppressBlurRef = useRef(false);

    useEffect(() => {
        if (!isEditingRef.current) {
            setDraft(effectiveWireValue ?? '');
        }
    }, [effectiveWireValue]);

    const commit = (nextValue: string | null | undefined) => {
        isEditingRef.current = false;
        if (nextValue === undefined) {
            setDraft(effectiveWireValue ?? '');
            return;
        }
        if (nextValue === (effectiveWireValue ?? null)) {
            return;
        }
        queue.setFieldValue(section, rowKey, field, nextValue);
    };

    const handleBlur = () => {
        if (suppressBlurRef.current) {
            suppressBlurRef.current = false;
            return;
        }
        commit(toWireValue(field, draft));
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        if (event.key === 'Enter') {
            (event.target as HTMLElement).blur();
        } else if (event.key === 'Escape') {
            suppressBlurRef.current = true;
            setDraft(effectiveWireValue ?? '');
            isEditingRef.current = false;
            (event.target as HTMLElement).blur();
        }
    };

    if (!isEditable) {
        return (
            <ReadOnlyConsiderationCell
                field={field}
                wireValue={wireValue}
                className={cellClassName}
                fieldChange={fieldChange}
                engagementId={engagementId}
            />
        );
    }

    return (
        <td className={cellClassName}>
            <span className="ff-editable-cell__value">
                <EditableConsiderationCellInput
                    kind={kind}
                    draft={draft}
                    onDraftChange={setDraft}
                    onCommit={(value) => commit(toWireValue(field, value))}
                    onFocus={() => (isEditingRef.current = true)}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                />
                <EditableConsiderationCellHistory
                    field={field}
                    fieldChange={fieldChange}
                    engagementId={engagementId}
                />
            </span>
        </td>
    );
};
